/**
 * Organization Features Page - Sprint H2.4
 * 
 * Platform admin page for managing per-organization feature flags.
 * - View available features with tier defaults
 * - Enable/disable features with mandatory reason
 * - View audit history of changes
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  ArrowLeft, Shield, Check, X, Loader2, AlertCircle,
  ToggleLeft, ToggleRight, History, Building2, Clock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Tier badge styling
const tierBadgeStyles = {
  a_free: 'bg-slate-100 text-slate-700 border-slate-300',
  a_pro: 'bg-blue-50 text-blue-700 border-blue-300',
  b_dedicated: 'bg-purple-50 text-purple-700 border-purple-300',
  c_onprem: 'bg-emerald-50 text-emerald-700 border-emerald-300',
};

const tierDisplayNames = {
  a_free: 'Free',
  a_pro: 'Pro',
  b_dedicated: 'Dedicated',
  c_onprem: 'On-Prem',
};

const OrgFeaturesPage = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { user, loading: authLoading } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [features, setFeatures] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  
  // Modal state
  const [toggleModal, setToggleModal] = useState({ open: false, feature: null, action: null });
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  const tr = t.adminFeatures || {};
  
  // Check if user is platform admin
  const isPlatformAdmin = user?.role === 'platform_admin';
  
  // Fetch features
  const fetchFeatures = useCallback(async () => {
    if (!orgId) return;
    
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/admin/orgs/${orgId}/features`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch features: ${response.status}`);
      }
      
      const data = await response.json();
      setFeatures(data);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching features:', err);
    } finally {
      setLoading(false);
    }
  }, [orgId]);
  
  // Fetch audit log
  const fetchAuditLog = useCallback(async () => {
    if (!orgId) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/admin/orgs/${orgId}/features/audit?page_size=50`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setAuditLog(data.items || []);
      }
    } catch (err) {
      console.error('Error fetching audit log:', err);
    }
  }, [orgId]);
  
  useEffect(() => {
    if (!authLoading && user?.role === 'platform_admin') {
      fetchFeatures();
      fetchAuditLog();
    }
  }, [fetchFeatures, fetchAuditLog, user, authLoading]);
  
  // Handle feature toggle
  const handleToggle = (feature, action) => {
    setToggleModal({ open: true, feature, action });
    setReason('');
  };
  
  const confirmToggle = async () => {
    if (reason.trim().length < 10) {
      toast.error(tr.reasonTooShort || 'Reason must be at least 10 characters');
      return;
    }
    
    const { feature, action } = toggleModal;
    
    try {
      setSubmitting(true);
      
      const response = await fetch(
        `${API_BASE}/api/admin/orgs/${orgId}/features/${feature.flag}/${action}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ reason: reason.trim() }),
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail?.message_en || errorData.detail?.error || 'Failed to toggle feature');
      }
      
      toast.success(action === 'enable' 
        ? (tr.featureEnabled || 'Feature enabled successfully')
        : (tr.featureDisabled || 'Feature disabled successfully')
      );
      
      setToggleModal({ open: false, feature: null, action: null });
      fetchFeatures();
      fetchAuditLog();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };
  
  // Access denied check - wait for auth to complete
  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }
  
  if (!isPlatformAdmin) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">
              {tr.accessDenied || 'Access Denied'}
            </h2>
            <p className="text-slate-600 mb-4">
              {tr.platformAdminOnly || 'Only platform administrators can manage feature flags.'}
            </p>
            <Button onClick={() => navigate(-1)} variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {tr.backToOrgs || 'Go Back'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">
              {tr.error || 'Error'}
            </h2>
            <p className="text-slate-600 mb-4">{error}</p>
            <Button onClick={fetchFeatures} variant="outline">
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/usage')}
            className="mb-4"
            data-testid="back-to-orgs-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {tr.backToOrgs || 'Back to Organizations'}
          </Button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Shield className="w-6 h-6 text-blue-600" />
                {tr.title || 'Feature Flags'}
              </h1>
              <p className="text-slate-600 mt-1">
                {tr.subtitle || 'Manage per-organization features'}
              </p>
            </div>
            
            <Button
              variant="outline"
              onClick={() => setShowAudit(!showAudit)}
              data-testid="toggle-audit-btn"
            >
              <History className="w-4 h-4 mr-2" />
              {tr.auditLog || 'Audit Log'}
            </Button>
          </div>
        </div>
        
        {/* Organization Info */}
        {features && (
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <Building2 className="w-10 h-10 text-slate-400" />
                <div>
                  <h2 className="text-lg font-semibold text-slate-900" data-testid="org-name">
                    {features.org_name}
                  </h2>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge 
                      variant="outline" 
                      className={tierBadgeStyles[features.tier] || ''}
                      data-testid="org-tier"
                    >
                      {tierDisplayNames[features.tier] || features.tier}
                    </Badge>
                    <span className="text-sm text-slate-500">
                      {features.effective.length} {tr.effectiveFeatures || 'effective features'}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Features List */}
          <div className={showAudit ? 'lg:col-span-2' : 'lg:col-span-3'}>
            <Card>
              <CardHeader>
                <CardTitle>{tr.availableFeatures || 'Available Features'}</CardTitle>
                <CardDescription>
                  {language === 'cs' 
                    ? 'Klikněte pro povolení nebo zakázání funkcí'
                    : 'Click to enable or disable features'
                  }
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3" data-testid="features-list">
                  {features?.available_features.map((feature) => (
                    <div
                      key={feature.flag}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 transition-colors"
                      data-testid={`feature-row-${feature.flag}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900">
                            {language === 'cs' ? feature.description_cs : feature.description_en}
                          </span>
                          {feature.is_default_for_tier && (
                            <Badge variant="secondary" className="text-xs">
                              {tr.defaultForTier || 'Default'}
                            </Badge>
                          )}
                        </div>
                        <span className="text-sm text-slate-500 font-mono">
                          {feature.flag}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        {feature.is_enabled ? (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300">
                            <Check className="w-3 h-3 mr-1" />
                            {tr.enabled || 'Enabled'}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-slate-500">
                            <X className="w-3 h-3 mr-1" />
                            {tr.disabled || 'Disabled'}
                          </Badge>
                        )}
                        
                        <Button
                          size="sm"
                          variant={feature.is_enabled ? 'outline' : 'default'}
                          onClick={() => handleToggle(feature, feature.is_enabled ? 'disable' : 'enable')}
                          disabled={feature.is_default_for_tier && feature.is_enabled}
                          data-testid={`toggle-${feature.flag}-btn`}
                        >
                          {feature.is_enabled ? (
                            <>
                              <ToggleRight className="w-4 h-4 mr-1" />
                              {tr.disable || 'Disable'}
                            </>
                          ) : (
                            <>
                              <ToggleLeft className="w-4 h-4 mr-1" />
                              {tr.enable || 'Enable'}
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Audit Log Sidebar */}
          {showAudit && (
            <div className="lg:col-span-1">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <History className="w-5 h-5" />
                    {tr.auditLog || 'Audit Log'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-[600px] overflow-y-auto" data-testid="audit-log">
                    {auditLog.length === 0 ? (
                      <p className="text-slate-500 text-sm text-center py-4">
                        {tr.noAuditEntries || 'No audit entries yet'}
                      </p>
                    ) : (
                      auditLog.map((entry, idx) => (
                        <div
                          key={entry.id || idx}
                          className="p-3 bg-slate-50 rounded-lg text-sm"
                          data-testid={`audit-entry-${idx}`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <Badge 
                              variant={entry.action === 'enable' ? 'default' : 'secondary'}
                              className={entry.action === 'enable' 
                                ? 'bg-emerald-100 text-emerald-700' 
                                : 'bg-amber-100 text-amber-700'
                              }
                            >
                              {entry.action === 'enable' ? tr.enabled : tr.disabled}
                            </Badge>
                            <span className="text-xs text-slate-400">
                              {new Date(entry.timestamp).toLocaleString(language === 'cs' ? 'cs-CZ' : 'en-US')}
                            </span>
                          </div>
                          <p className="font-mono text-xs text-slate-600 mb-1">
                            {entry.feature}
                          </p>
                          <p className="text-slate-500 text-xs italic">
                            "{entry.reason}"
                          </p>
                          <p className="text-xs text-slate-400 mt-1">
                            {entry.admin_email || entry.admin_user_id}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
      
      {/* Toggle Confirmation Modal */}
      <Dialog open={toggleModal.open} onOpenChange={(open) => !open && setToggleModal({ open: false, feature: null, action: null })}>
        <DialogContent data-testid="toggle-modal">
          <DialogHeader>
            <DialogTitle>
              {toggleModal.action === 'enable' 
                ? (tr.enableFeature || 'Enable Feature') 
                : (tr.disableFeature || 'Disable Feature')
              }
            </DialogTitle>
            <DialogDescription>
              {toggleModal.feature && (
                <span className="font-mono text-blue-600">
                  {toggleModal.feature.flag}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              {tr.reason || 'Reason'} <span className="text-red-500">*</span>
            </label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={tr.reasonPlaceholder || 'Explain why this change is being made (min 10 characters)...'}
              className={`min-h-[100px] ${reason.length < 10 && reason.length > 0 ? 'border-red-300' : ''}`}
              data-testid="reason-textarea"
            />
            <p className={`text-xs mt-1 ${reason.length < 10 ? 'text-red-500' : 'text-slate-400'}`}>
              {reason.length}/10 {language === 'cs' ? 'min. znaků' : 'min. characters'}
            </p>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setToggleModal({ open: false, feature: null, action: null })}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmToggle}
              disabled={submitting || reason.trim().length < 10}
              variant={toggleModal.action === 'enable' ? 'default' : 'destructive'}
              data-testid="confirm-toggle-btn"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {toggleModal.action === 'enable' 
                ? (tr.confirmEnable || 'Confirm Enable')
                : (tr.confirmDisable || 'Confirm Disable')
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrgFeaturesPage;
